import { t } from '@lingui/core/macro'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { createExclusiveEvalAction } from 'features/evaluations/metadata/utils/persistEvalDecision'
import { EvalPagePrefetchers } from 'features/knowledgeDocumentViewer/components/metadata/EvalPagePrefetchers'
import { useKnowledgeDocumentUXContext } from 'features/knowledgeDocumentViewer/contexts/KnowledgeDocumentUXContext'
import { usePipelineConfig } from 'features/knowledgeDocumentViewer/contexts/PipelineConfigContext'
import { useEvalPageQueue } from 'features/knowledgeDocumentViewer/hooks/useEvalPageQueue'
import type { EvalPagePool } from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalPool'
import {
  fetchBrowseEvalPages,
  fetchNextEvalPages,
} from 'features/knowledgeDocumentViewer/utils/pageMetadataEvalSampling'
import { useGeckoNotifications } from 'features/notifications'
import { useGeckoPermissions } from 'features/permissions'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'

type EvalSession = ReturnType<typeof useEvalPageQueue> & {
  isBusy: boolean
  pageIsOnScreen: boolean
  runExclusive: (action: () => Promise<void>) => Promise<void>
  next: (exitWhenEmpty?: boolean) => Promise<void>
}

const PageMetadataEvalSessionContext = createContext<EvalSession | undefined>(
  undefined
)

export function PageMetadataEvalSessionProvider({
  browseSessionId,
  pool,
  pageClassifications,
  issuers,
  children,
}: {
  browseSessionId?: string
  pool: EvalPagePool
  pageClassifications: readonly string[] | undefined
  issuers: readonly string[] | undefined
  children: React.ReactNode
}) {
  const { authorizedAxios } = useGeckoAuth()
  const { user } = useGeckoPermissions()
  const { POWER_BACKEND_URL } = useRuntimeVariables()
  const {
    siteId,
    activeTabId,
    currentDocumentId,
    currentPageNumber,
    pushToHistory,
    updateDocumentHistoryItem,
    onExitEvalSession,
  } = useKnowledgeDocumentUXContext()
  const {
    selectedPipelineConfigId,
    isPreviewMode,
    isPipelineConfigScopeLoading,
    pipelineConfigScopeError,
    retryPipelineConfigScope,
  } = usePipelineConfig()
  const { infoNotification } = useGeckoNotifications()
  const scopeKey = JSON.stringify([
    ...(browseSessionId === undefined ? [] : [browseSessionId]),
    siteId,
    user?.email,
    pool,
    pageClassifications,
    issuers,
    selectedPipelineConfigId,
  ])
  const scopeRef = useRef(scopeKey)
  scopeRef.current = scopeKey
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const samplePages = useCallback(
    (limit: number, excludePageIds: string[]) => {
      const args = {
        axios: authorizedAxios,
        backendUrl: POWER_BACKEND_URL,
        siteId,
        pool,
        excludePageIds,
        previewMode: isPreviewMode,
        limit,
        pageClassifications,
        issuers,
      }
      return pool === 'not_labeled_by_me'
        ? fetchNextEvalPages(args)
        : fetchBrowseEvalPages(args)
    },
    [
      POWER_BACKEND_URL,
      authorizedAxios,
      siteId,
      pool,
      isPreviewMode,
      pageClassifications,
      issuers,
    ]
  )
  const enabled =
    !isPipelineConfigScopeLoading && !!selectedPipelineConfigId && !!user?.email
  const isBrowse = pool !== 'not_labeled_by_me'
  const queue = useEvalPageQueue({
    enabled,
    scopeKey,
    pool,
    samplePages,
    snapshot: isBrowse,
  })
  const [isBusy, setIsBusy] = useState(false)
  const [runExclusive] = useState(() => createExclusiveEvalAction(setIsBusy))
  const { currentPage, advance } = queue
  const pageIsOnScreen =
    currentPage !== undefined &&
    currentPage.document_id === currentDocumentId &&
    currentPage.page_num === currentPageNumber
  const next = useCallback(
    async (exitWhenEmpty = false) => {
      const requestScope = scopeKey
      const result = await advance()
      if (!mounted.current || scopeRef.current !== requestScope) return
      if (result === 'empty') {
        infoNotification(t`No more pages in this queue`)
        if (exitWhenEmpty) onExitEvalSession?.()
      }
    },
    [advance, infoNotification, onExitEvalSession, scopeKey]
  )

  useEffect(() => {
    if (!currentPage) return
    if (currentPage.document_id === currentDocumentId) {
      if (currentPage.page_num !== currentPageNumber)
        updateDocumentHistoryItem(activeTabId, {
          pageNumber: currentPage.page_num,
          selectedBoxIds: [],
        })
    } else {
      pushToHistory(activeTabId, {
        type: 'document',
        documentId: currentPage.document_id,
        pageNumber: currentPage.page_num,
        selectedBoxIds: [],
        query: '',
      })
    }
  }, [
    activeTabId,
    currentDocumentId,
    currentPage,
    currentPageNumber,
    pushToHistory,
    updateDocumentHistoryItem,
  ])

  const scopeError =
    pipelineConfigScopeError ??
    (!isPipelineConfigScopeLoading && !selectedPipelineConfigId
      ? new Error('No pipeline config is available for this session')
      : undefined)
  const retry = async () => {
    if (!scopeError) return queue.retry()
    try {
      await retryPipelineConfigScope()
      return true
    } catch {
      // The pipeline scope exposes the request error to the session view.
      return false
    }
  }
  return (
    <PageMetadataEvalSessionContext.Provider
      value={{
        ...queue,
        error: scopeError ?? queue.error,
        retry,
        isBusy,
        pageIsOnScreen,
        runExclusive,
        next,
      }}>
      <EvalPagePrefetchers
        key={scopeKey}
        queuedPages={queue.queuedPages}
        trail={queue.trail}
        siteId={siteId}
      />
      {children}
    </PageMetadataEvalSessionContext.Provider>
  )
}

export function usePageMetadataEvalSession(): EvalSession {
  const session = useContext(PageMetadataEvalSessionContext)
  if (!session) throw new Error('Metadata eval requires a labeling session')
  return session
}

export function useOptionalPageMetadataEvalSession(): EvalSession | undefined {
  return useContext(PageMetadataEvalSessionContext)
}
